import React, { useEffect, useState } from "react";
import { AlertCircle, Phone, RefreshCw, IndianRupee, Printer } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { printOverdueCustomers } from "@/lib/print";

export default function OverdueCustomers() {
  const { business, settings } = useSettings();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOverdueCustomers = async () => {
    setLoading(true);

    try {
      const res = await api.get("/overdue-customers");
      setCustomers(
        Array.isArray(res.data?.customers)
          ? res.data.customers
          : []
      );
    } catch (e) {
      console.error("Failed to load overdue customers:", e);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverdueCustomers();
  }, []);

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy">
            Overdue Customers
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            Customers with pending overdue amounts
          </p>
        </div>

        <button
          onClick={loadOverdueCustomers}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-maroon text-white text-sm font-medium hover:bg-maroon-hover disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-red-50 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>

            <div>
              <p className="text-sm text-slate-500">
                Overdue Customers
              </p>

              <p className="text-2xl font-bold text-navy">
                {customers.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-red-50 flex items-center justify-center">
              <IndianRupee className="h-5 w-5 text-red-600" />
            </div>

            <div>
              <p className="text-sm text-slate-500">
                Total Overdue
              </p>

              <p className="text-2xl font-bold text-red-600">
                ₹
                {formatAmount(
                  customers.reduce(
                    (total, customer) =>
                      total + Number(customer.overdue || 0),
                    0
                  )
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading font-semibold text-navy">
            Overdue Loan Accounts
          </h2>

          <button
            type="button"
            onClick={() =>
              printOverdueCustomers(
                customers,
                customers.reduce(
                  (total, customer) =>
                    total + Number(customer.overdue || 0),
                  0
                ),
                business,
                settings
              )
            }
            disabled={loading || customers.length === 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            Print / Save PDF
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500">
            Loading overdue customers...
          </div>
        ) : customers.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-green-50 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-green-600" />
            </div>

            <h3 className="mt-4 font-semibold text-navy">
              No overdue customers
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              All loan accounts are currently up to date.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Sl.No
                  </th>

                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Customer
                  </th>

                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Phone
                  </th>

                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Loan Number
                  </th>

                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Overdue
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {customers.map((customer, index) => (
                  <tr
                    key={`${customer.loan_id}-${customer.customer_id}`}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-5 py-4 text-sm font-medium text-slate-600">
                      {index + 1}
                    </td>

                    <td className="px-5 py-4">
                      <div className="font-medium text-navy">
                        {customer.customer_name || "Unknown Customer"}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      {customer.phone ? (
                        <a
                          href={`tel:${customer.phone}`}
                          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-maroon"
                        >
                          <Phone className="h-4 w-4" />
                          {customer.phone}
                        </a>
                      ) : (
                        <span className="text-sm text-slate-400">
                          No phone
                        </span>
                      )}
                    </td>

                    <td className="px-5 py-4">
                      <span className="text-sm text-slate-600">
                        {customer.loan_number || "-"}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-right">
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 font-semibold text-sm">
                        ₹{formatAmount(customer.overdue)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
